/*----------------------------------
- DOMAINS LANDING CTA SECTION
----------------------------------*/

/**
 * The closing block as the mock draws it (`dm-cta`, docs/mockups/website-domains.html): the Home hero's two-line
 * headline with the butter mark (`wp-mark`) on the phrase the visitor searched for, one paragraph with the search's
 * count and the plan facts, the persona pair; then the product film and the search's figures (`dl.dm-stats`).
 *
 * Most visitors arrive cold from ChatGPT, GitHub or Google looking for a "one word domains database", so the copy's
 * one job is to say that the database is the entry point and not the product. It claims only what the product and
 * Full Access really do (ADR-0083: no free plan, so the paragraph ends on the price and the refund, read from their
 * constants in the catalog).
 *
 * The page H1 lives in the title block above the table, so this heading is an h2.
 *
 * @docs docs/features/website-domains-hubs
 * @rule Every figure is the search's own (`stats.count`, `stats.medianAsk`) or absent: a tile with no value is
 *       dropped, never filled, and a sampled count carries its mark and its spoken "(estimated)" (ADR-0034).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';
import {
    BROWSE_ESTIMATE_SPOKEN_LABEL,
    BROWSE_ESTIMATE_SYMBOL,
} from '@uniquedomains/components/radar/catalogs/browseResultCountCopy';

import { DOMAINS_CLOSING_COPY, DOMAINS_STATS_COPY } from '@/client/catalogs/landing/domains/pageCopy';
import { formatCompactStat } from '@/client/pages/landing/lib/formatCompactStat';
import DomainsFilm from './DomainsFilm';
import PersonaPair from '@/client/pages/landing/components/conversion/PersonaPair';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsLandingCtaSectionProps = {
    medianAsk: number | null;
    totalCountLabel: string | null;
    /** Adds the spoken "(estimated)" to the tracked-names tile when the total is sampled. */
    countPrecision: DatabaseBrowseCountPrecision;
    totalTlds: number | null;
};

type DomainsStat = { label: string; value: string; isEstimate: boolean };

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsLandingCtaSection = ({ medianAsk, totalCountLabel, countPrecision, totalTlds }: DomainsLandingCtaSectionProps) => {
    const candidates: (DomainsStat | null)[] = [
        totalCountLabel
            ? { label: DOMAINS_STATS_COPY.tracked, value: totalCountLabel, isEstimate: countPrecision === 'sampled' }
            : null,
        // Drawn from the same page sample as the count on a catalog-wide search, so it carries the
        // same mark (Gaetan, D1 of 2026-10-12): it moves between nights.
        medianAsk
            ? {
                  label: DOMAINS_STATS_COPY.medianAsk,
                  value: `${countPrecision === 'sampled' ? BROWSE_ESTIMATE_SYMBOL : ''}$${formatCompactStat(medianAsk)}`,
                  isEstimate: countPrecision === 'sampled',
              }
            : null,
        totalTlds ? { label: DOMAINS_STATS_COPY.tlds, value: formatCompactStat(totalTlds), isEstimate: false } : null,
    ];
    const stats = candidates.filter((stat): stat is DomainsStat => Boolean(stat));

    return (
        <>
            <section class="wp-sec dm-cta" data-testid="domains-footer-product-entry-card">
                <h2 class="dm-cta__h">
                    <span>
                        {DOMAINS_CLOSING_COPY.headlineLead} <span class="wp-mark">{DOMAINS_CLOSING_COPY.headlineMark}</span>
                        {DOMAINS_CLOSING_COPY.headlineEnd}
                    </span>
                    <span class="dm-cta__l2">
                        {DOMAINS_CLOSING_COPY.answerLead} <b>{DOMAINS_CLOSING_COPY.answerMark}</b> {DOMAINS_CLOSING_COPY.answerEnd}
                    </span>
                </h2>
                <p class="dm-cta__p">{DOMAINS_CLOSING_COPY.paragraph(totalCountLabel)}</p>
                <PersonaPair
                    attribution="website_domains_footer"
                    investorTriggerId="website_domains_footer_investor"
                    founderTriggerId="website_domains_footer_founder"
                    page="domains_public"
                    section="domains_footer"
                    surface="footer"
                    labels={DOMAINS_CLOSING_COPY.personaCtas}
                    class="dm-cta__acts"
                />
            </section>

            <DomainsFilm />

            {stats.length ? (
                <dl class="dm-stats" aria-label={DOMAINS_STATS_COPY.label}>
                    {stats.map((stat) => (
                        // `dt` before `dd`, as a description list reads; the value still sits on top (`column-reverse`).
                        <div key={stat.label}>
                            <dt class="dm-stat__l">{stat.label}</dt>
                            <dd class="dm-stat__v">
                                {stat.value}
                                {stat.isEstimate ? <span class="ud-sr-only"> {BROWSE_ESTIMATE_SPOKEN_LABEL}</span> : null}
                            </dd>
                        </div>
                    ))}
                </dl>
            ) : null}
        </>
    );
};

export default DomainsLandingCtaSection;
